import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Heart, Clock, Star, Users, CheckCircle, ChevronDown, Phone, Award, Zap, UserCheck, Target, Repeat, CalendarCheck, ThumbsUp, TrendingUp, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import CostSimulator from '../components/CostSimulator';
import { SERVICES } from '../lib/services-data';
import { COMPANY_INFO, formatPrice } from '../lib/utils';
import axios from 'axios';
import { SEOHead, localBusinessSchema } from '../components/SEOHead';
import { GoogleReviewsSection, useGoogleReviews } from '../components/GoogleReviews';
import { AGENCIES } from '../lib/agencies-data';
import { getMainCities, cityPath } from '../lib/cities-data';
import { swallowWithLog } from '../lib/logger';
import { BlogCard } from '../components/BlogCard';
import { VIEWPORT_ONCE } from '../lib/motion';
import { CHOUETTE_PATH, CHOUETTE_ICON, CHOUETTE_SCREENSHOTS } from '../lib/chouette-data';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

const TRUST_ITEMS = [
  { icon: Shield, title: 'Fiabilité', desc: 'Nos Easers sont soigneusement sélectionnés, formés et suivis pour garantir un service de qualité supérieure.', img: 'https://ptitservices.com/sitepad-data/uploads/2025/12/Gemini_Generated_Image_t4gzz6t4gzz6t4gz.png' },
  { icon: UserCheck, title: 'Easer dédié', desc: 'Un interlocuteur unique qui connaît vos habitudes et votre domicile. Continuité et confiance garanties.', img: 'https://ptitservices.com/sitepad-data/uploads/2025/12/Gemini_Generated_Image_fr03nxfr03nxfr03.png' },
  { icon: Zap, title: 'Avance immédiate', desc: 'Vous ne payez que le reste à charge grâce à l\'avance immédiate du crédit d\'impôt (Urssaf).', img: 'https://ptitservices.com/sitepad-data/uploads/2025/12/Gemini_Generated_Image_f0jcbsf0jcbsf0jc.png' },
  { icon: Heart, title: 'Satisfaction garantie', desc: 'Nous garantissons la qualité de nos services avec des solutions qui répondent à vos besoins.', img: 'https://ptitservices.com/sitepad-data/uploads/2025/12/Gemini_Generated_Image_y4j0idy4j0idy4j0.png' },
];

const PROCESS_STEPS = [
  { num: '01', title: 'Étape initiale', desc: 'Contactez-nous par téléphone ou par mail. Nous fixerons un rendez-vous gratuit et sans engagement.' },
  { num: '02', title: 'Rendez-vous', desc: 'Évaluation de vos besoins, devis personnalisé, inscription à l\'avance immédiate et planification des interventions.' },
  { num: '03', title: 'Prestation de service', desc: 'Votre Easer intervient selon les modalités convenues. En fin de prestation, vous signez le bon d\'intervention.' },
  { num: '04', title: 'Suivi qualité', desc: 'Nous nous assurons de votre satisfaction à chaque intervention. Contrôles réguliers et écoute permanente.' },
];

const FAQ_ITEMS = [
  { q: 'Dois-je avancer les frais de la prestation ?', a: 'Non ! Grâce à l\'avance immédiate du crédit d\'impôt, vous ne payez que le reste à charge (50% du coût total). Le crédit d\'impôt est directement déduit.' },
  { q: 'Qui est éligible au crédit d\'impôt ?', a: 'Tous les contribuables français, qu\'ils soient actifs, retraités ou demandeurs d\'emploi, peuvent bénéficier du crédit d\'impôt de 50% pour les services à la personne.' },
  { q: 'Quelle est la durée d\'engagement ?', a: 'Nous proposons des services ponctuels sans engagement, et des services réguliers avec engagement de 12 mois à tarif préférentiel.' },
  { q: 'Quels types de services proposez-vous ?', a: 'Nous couvrons tous les besoins du domicile : ménage, repassage, vitres, courses, jardinage, petit bricolage, aide aux repas, assistance administrative. Découvrez nos offres Global Service tout inclus.' },
  { q: 'Comment activer l\'avance immédiate ?', a: 'C\'est très simple ! Lors de notre rendez-vous, nous nous occupons de toutes les démarches d\'inscription à l\'avance immédiate auprès de l\'Urssaf.' },
  { q: 'Qu\'est-ce que l\'offre Chouette Apprentissage ?', a: 'Tous nos clients disposant d\'un abonnement actif bénéficient gratuitement du Pack Famille de l\'application d\'aide aux devoirs Chouette Apprentissage (CP à la Terminale, valeur 5 €/mois). C\'est automatique : il suffit de vous signaler comme client Ptitservices.com dans l\'application.' },
];

export default function HomePage() {
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average: 0, total: 0 });
  const [blogPosts, setBlogPosts] = useState([]);
  const googleReviews = useGoogleReviews();

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/blog`, { params: { limit: 3 } })
      .then((r) => setBlogPosts(r.data.items || []))
      .catch(swallowWithLog('blog home'));
  }, []);
  const ratingForSchema = googleReviews?.enabled && googleReviews.count > 0
    ? { value: googleReviews.rating, count: googleReviews.count }
    : (reviewStats.total > 0 ? { value: reviewStats.average, count: reviewStats.total } : null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/reviews?limit=6`)
      .then(res => {
        if (cancelled) return;
        setReviews(res.data.reviews);
        setReviewStats(res.data.stats);
      })
      .catch(swallowWithLog('avis accueil'));
    return () => { cancelled = true; };
  }, []);
  return (
    <div>
      <SEOHead
        title="Ménage et jardinage à domicile dans l'Artois"
        description="Ptitservices.com, facilitateur de vies. Services à la personne : ménage, jardinage, repassage, petit bricolage dans l'Artois. Crédit d'impôt 50% avec avance immédiate. Ménage dès 14,99€/h, jardinage dès 24,95€/h."
        path="/"
        schema={localBusinessSchema(AGENCIES['artois.ptitservices'], { rating: ratingForSchema })}
      />
      {/* ====== HERO ====== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div className="lg:col-span-7" initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeUp}>
                <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
                  Facilitateur de vies &bull; Services à la personne
                </Badge>
              </motion.div>
              <motion.h1 variants={fadeUp} className="font-['Space_Grotesk'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-4">
                Ménage & jardinage à domicile —{' '}
                <span className="text-[hsl(var(--primary))]">payez seulement le reste à charge</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6 max-w-xl">
                Crédit d'impôt 50% + Avance immédiate (Urssaf). Votre Easer dédié facilite votre quotidien avec bienveillance et professionnalisme. <strong>Tarifs transparents, tout est clair, pas de surprise.</strong>
              </motion.p>

              {/* Price chips */}
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3 mb-8">
                <div className="px-4 py-2.5 rounded-xl bg-card border border-border shadow-sm hover:shadow-md hover:border-[hsl(var(--accent))] transition-shadow cursor-default" data-testid="hero-price-cleaning">
                  <span className="text-xs text-muted-foreground block">Ménage — à partir de</span>
                  <span className="text-sm line-through text-muted-foreground">29,99 €/h</span>
                  <span className="text-lg font-bold text-[hsl(var(--primary))] ml-2">14,99 €/h</span>
                </div>
                <div className="px-4 py-2.5 rounded-xl bg-card border border-border shadow-sm hover:shadow-md hover:border-[hsl(var(--accent))] transition-shadow cursor-default" data-testid="hero-price-gardening">
                  <span className="text-xs text-muted-foreground block">Jardinage — à partir de</span>
                  <span className="text-sm line-through text-muted-foreground">49,90 €/h</span>
                  <span className="text-lg font-bold text-[hsl(var(--primary))] ml-2">24,95 €/h</span>
                </div>
              </motion.div>

              {/* CTAs */}
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <Link to="/devis">
                  <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg" data-testid="hero-quote-button">
                    Demander un devis gratuit
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/credit-impot">
                  <Button size="lg" variant="outline" data-testid="hero-tax-credit-link">
                    Comprendre l'avance immédiate
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            {/* Hero image */}
            <motion.div className="lg:col-span-5" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600&h=450&fit=crop" 
                  alt="Intérieur lumineux et propre - Ptitservices" 
                  className="w-full h-[300px] sm:h-[380px] object-cover"
                  loading="eager"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur rounded-xl p-3 shadow-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center">
                      <UserCheck className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-semibold block">Easer dédié</span>
                      <span className="text-xs text-muted-foreground">Un interlocuteur unique, votre confiance</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====== GLOBAL SERVICE HIGHLIGHT (OFFRES PHARES) ====== */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Nos offres phares</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Offres Global Service — tout inclus, à la demande</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Nous mettons notre personnel à votre disposition pour la durée que vous choisissez. Tout est inclus, c'est vous qui décidez. Tarifs transparents, pas de surprise.</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="global-service-comparison">
            {/* Global Domicile */}
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Card className="p-6 sm:p-8 border-2 border-[hsl(var(--primary))]/20 bg-card shadow-lg h-full">
                <Badge className="mb-4 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]">Populaire</Badge>
                <h3 className="font-['Space_Grotesk'] text-xl font-semibold mb-2">Global Service Domicile</h3>
                <p className="text-sm text-muted-foreground mb-4">Votre Easer à disposition pour le temps que vous choisissez. Tout est inclus, à la demande.</p>
                <div className="mb-4">
                  <span className="text-xs text-muted-foreground">À partir de </span>
                  <span className="text-sm line-through text-muted-foreground">29,99 €/h</span>
                  <span className="text-2xl font-bold text-[hsl(var(--primary))] ml-2">14,99 €/h</span>
                  <span className="text-sm text-muted-foreground ml-1">après avance immédiate</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {['Ménage complet', 'Vitres', 'Repassage', 'Courses & Drives', 'Accompagnement personnalisé', 'Easer attitré', 'Suivi qualité'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-[hsl(var(--primary))] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground italic mb-4">NB : la charge de travail réalisable est en fonction de la durée de prestation choisie.</p>
                <Link to="/services/global-service-domicile">
                  <Button className="w-full" variant="outline">Découvrir l'offre <ArrowRight className="ml-2 h-4 w-4" /></Button>
                </Link>
              </Card>
            </motion.div>

            {/* Global Jardin */}
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Card className="p-6 sm:p-8 border-2 border-[hsl(var(--primary))]/20 bg-card shadow-lg h-full">
                <Badge className="mb-4 bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage))]">Saison</Badge>
                <h3 className="font-['Space_Grotesk'] text-xl font-semibold mb-2">Global Service Jardin</h3>
                <p className="text-sm text-muted-foreground mb-4">Votre jardinier à disposition pour le temps que vous choisissez. Tout est inclus.</p>
                <div className="mb-4">
                  <span className="text-xs text-muted-foreground">À partir de </span>
                  <span className="text-sm line-through text-muted-foreground">49,90 €/h</span>
                  <span className="text-2xl font-bold text-[hsl(var(--primary))] ml-2">24,95 €/h</span>
                  <span className="text-sm text-muted-foreground ml-1">après avance immédiate</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {['Tonte pelouse', 'Taille haies & arbustes', 'Désherbage', 'Ramassage feuilles', 'Jardinier attitré', 'Suivi saisonnier'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-[hsl(var(--primary))] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground italic mb-4">NB : la charge de travail réalisable est en fonction de la durée de prestation choisie.</p>
                <Link to="/services/global-service-jardin">
                  <Button className="w-full" variant="outline">Découvrir l'offre <ArrowRight className="ml-2 h-4 w-4" /></Button>
                </Link>
              </Card>
            </motion.div>
          </div>

          <div className="text-center mt-8">
            <Link to="/devis" data-testid="global-service-quote-button">
              <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white">
                Demander un devis Global Service
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ====== AVANTAGE CLIENT — CHOUETTE APPRENTISSAGE ====== */}
      <section className="py-12 sm:py-16 lg:py-20" data-testid="home-chouette-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Card className="overflow-hidden border-2 border-[hsl(var(--primary))]/20 shadow-lg">
              <div className="grid grid-cols-1 lg:grid-cols-12">
                <div className="lg:col-span-7 p-6 sm:p-8 lg:p-10">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Badge className="px-4 py-1.5 text-sm font-semibold rounded-full bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90">
                      <Gift className="h-3.5 w-3.5 mr-1.5" /> Avantage client
                    </Badge>
                    <Badge variant="outline" className="px-3 py-1.5 text-xs rounded-full">Nouveau</Badge>
                  </div>
                  <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">
                    L'aide aux devoirs de vos enfants, <span className="text-[hsl(var(--primary))]">offerte avec votre abonnement</span>
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Nous offrons le <strong>Pack Famille</strong> de l'application <strong>Chouette Apprentissage</strong> (valeur 5,00 €/mois) à tous nos clients disposant d'un abonnement actif. Signalez-vous simplement comme client dans l'application : l'abonnement est offert automatiquement. Un tuteur bienveillant qui accompagne vos enfants du <strong>CP à la Terminale</strong> : il ne donne jamais la réponse, il la fait trouver.
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-2 mb-6 text-sm">
                    {['Toutes les matières du programme', 'Aide en photo, dictées et jeux', 'Activation automatique dans l\'application', 'Sans publicité, données en France'].map((item) => (
                      <li key={item} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-[hsl(var(--primary))] flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-3">
                    <Link to={CHOUETTE_PATH}>
                      <Button className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="home-chouette-cta">
                        Découvrir l'offre <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/devis">
                      <Button variant="outline" data-testid="home-chouette-quote">Devenir client</Button>
                    </Link>
                  </div>
                </div>
                <div className="lg:col-span-5 relative bg-[hsl(var(--brand-mint))]/30 flex items-center justify-center p-6 sm:p-8 min-h-[260px]">
                  <img src={CHOUETTE_SCREENSHOTS[0].src} alt="Écran d'accueil de l'application Chouette Apprentissage" className="max-h-[320px] w-auto rounded-2xl shadow-2xl border border-border" loading="lazy" />
                  <img src={CHOUETTE_ICON} alt="Icône Chouette Apprentissage" className="absolute bottom-6 right-6 w-14 h-14 rounded-2xl shadow-lg" loading="lazy" />
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* ====== TRUST / WHY US ====== */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Notre vision</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Facilitateur de vies : notre métier, votre liberté</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Pour les <strong>actifs</strong>, nous libérons du temps pour profiter de la vie, de vos enfants, de vos passions. 
                Pour les <strong>retraités</strong>, nous permettons de rester chez soi le plus longtemps possible, en toute autonomie et dignité.
              </p>
              <Link to="/notre-vision">
                <Button variant="outline">
                  Découvrir notre vision
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl overflow-hidden shadow-md">
                  <img src="https://images.unsplash.com/photo-1758687126877-b37052a20a4d?w=400&h=280&fit=crop" alt="Famille heureuse" className="w-full h-[140px] sm:h-[170px] object-cover" loading="lazy" />
                </div>
                <div className="rounded-xl overflow-hidden shadow-md">
                  <img src="https://images.unsplash.com/photo-1658632302262-957f74b1fdd9?w=400&h=280&fit=crop" alt="Senior heureux chez soi" className="w-full h-[140px] sm:h-[170px] object-cover" loading="lazy" />
                </div>
                <div className="rounded-xl overflow-hidden shadow-md col-span-2">
                  <img src="https://images.unsplash.com/photo-1524386416438-98b9b2d4b433?w=800&h=280&fit=crop" alt="Jardin fleuri entretenu" className="w-full h-[140px] sm:h-[170px] object-cover" loading="lazy" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====== DIFFÉRENCIATION — FACILITATEUR DE VIES ====== */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Notre différence</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Bien plus qu'un prestataire de ménage : <span className="text-[hsl(var(--primary))]">facilitateur de vies</span></h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Notre métier n'est pas seulement de nettoyer, c'est de vous faciliter la vie au quotidien. Découvrez ce qui nous distingue de la concurrence.</p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
            <motion.div variants={fadeUp}>
              <Card className="p-6 h-full border border-border shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center">
                    <UserCheck className="h-5 w-5 text-[hsl(var(--primary))]" />
                  </div>
                  <h3 className="font-['Space_Grotesk'] font-semibold">Easer dédié et attitré</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">Chez la concurrence, vous n'avez jamais le même intervenant. Chez Ptitservices, votre Easer dédié connaît vos habitudes, votre domicile et vos préférences. Continuité, confiance et qualité garanties.</p>
              </Card>
            </motion.div>
            <motion.div variants={fadeUp}>
              <Card className="p-6 h-full border border-border shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center">
                    <Target className="h-5 w-5 text-[hsl(var(--primary))]" />
                  </div>
                  <h3 className="font-['Space_Grotesk'] font-semibold">Global Service tout inclus</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">Un seul prestataire, un seul contrat, une seule facture. Nous mettons notre personnel à votre disposition pour le temps que vous choisissez. Tout est inclus, c'est à la demande : ménage, vitres, linge, courses, jardinage, accompagnement...</p>
              </Card>
            </motion.div>
            <motion.div variants={fadeUp}>
              <Card className="p-6 h-full border border-border shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-[hsl(var(--primary))]" />
                  </div>
                  <h3 className="font-['Space_Grotesk'] font-semibold">Transparence totale</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">Nous mettons un point d'honneur sur la transparence de nos tarifs. Tout est clair, détaillé et communiqué avant chaque intervention. Pas de surprise, pas de frais cachés. Le devis fait foi.</p>
              </Card>
            </motion.div>
            <motion.div variants={fadeUp}>
              <Card className="p-6 h-full border border-border shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center">
                    <Zap className="h-5 w-5 text-[hsl(var(--primary))]" />
                  </div>
                  <h3 className="font-['Space_Grotesk'] font-semibold">Avance immédiate du crédit d'impôt</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">Nous gérons l'inscription à l'avance immédiate Urssaf pour vous. Résultat : vous ne payez que 50% du coût réel, directement déduit, sans attendre votre déclaration d'impôts.</p>
              </Card>
            </motion.div>
            <motion.div variants={fadeUp}>
              <Card className="p-6 h-full border border-border shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center">
                    <Repeat className="h-5 w-5 text-[hsl(var(--primary))]" />
                  </div>
                  <h3 className="font-['Space_Grotesk'] font-semibold">Ponctuel ou régulier : à vous de choisir</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">Besoin d'un grand ménage ponctuel ou d'un entretien hebdomadaire ? Nous nous adaptons à vos besoins avec des interventions ponctuelles ou régulières, sans obligation pour les prestations occasionnelles.</p>
              </Card>
            </motion.div>
            <motion.div variants={fadeUp}>
              <Card className="p-6 h-full border border-border shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center">
                    <Heart className="h-5 w-5 text-[hsl(var(--primary))]" />
                  </div>
                  <h3 className="font-['Space_Grotesk'] font-semibold">Bienveillance et proximité</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">Notre approche est fondée sur la bienveillance, le respect et la proximité. Nous sommes à l'écoute de vos besoins et nous adaptons nos services pour vous offrir un accompagnement sur mesure dans l'Artois.</p>
              </Card>
            </motion.div>
          </motion.div>

          {/* Interventions ponctuelles et régulières */}
          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
            <motion.div variants={fadeUp}>
              <Card className="p-6 border-2 border-[hsl(var(--primary))]/10 bg-[hsl(var(--brand-mint))]/20">
                <div className="flex items-center gap-3 mb-3">
                  <CalendarCheck className="h-6 w-6 text-[hsl(var(--primary))]" />
                  <h3 className="font-['Space_Grotesk'] font-semibold">Interventions régulières</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">Hebdomadaire, bi-mensuel ou mensuel : planifiez vos interventions et bénéficiez d'un tarif préférentiel avec engagement 12 mois. Votre Easer dédié intervient selon un planning défini ensemble.</p>
                <Badge className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]">Tarif préférentiel</Badge>
              </Card>
            </motion.div>
            <motion.div variants={fadeUp}>
              <Card className="p-6 border-2 border-[hsl(var(--primary))]/10 bg-[hsl(var(--brand-mint))]/20">
                <div className="flex items-center gap-3 mb-3">
                  <ThumbsUp className="h-6 w-6 text-[hsl(var(--primary))]" />
                  <h3 className="font-['Space_Grotesk'] font-semibold">Interventions ponctuelles</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">Grand ménage de printemps, préparation d'un événement, coup de main ponctuel : nous intervenons quand vous en avez besoin, sans engagement. Flexibilité totale.</p>
                <Badge className="bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]">Sans engagement</Badge>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ====== SERVICES ====== */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Nos services</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Découvrez nos services</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Une gamme complète de services pour améliorer votre quotidien et profiter de votre temps libre.</p>
          </motion.div>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
            {SERVICES.map((service) => (
              <motion.div key={service.id} variants={fadeUp}>
                <Link to={`/services/${service.id}`}>
                  <Card className="p-6 h-full bg-card border border-border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer group" data-testid="service-card">
                    {service.isGlobal && (
                      <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Global Service</Badge>
                    )}
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center mb-3">
                      <service.icon className="h-5 w-5 text-[hsl(var(--primary))]" />
                    </div>
                    <h3 className="font-['Space_Grotesk'] font-semibold mb-1 group-hover:text-[hsl(var(--primary))] transition-colors">{service.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{service.shortDesc}</p>
                    <div className="flex items-center justify-end">
                      <span className="text-xs text-muted-foreground flex items-center gap-1 group-hover:text-[hsl(var(--primary))]">
                        En savoir plus <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
          <div className="text-center mt-6">
            <Link to="/services">
              <Button variant="outline" size="lg">
                Voir tous les services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ====== SIMULATOR ====== */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Simulateur</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Calculez vos économies</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Grâce au crédit d'impôt de 50% et à l'avance immédiate, vous ne payez que la moitié du coût de nos services. Utilisez notre simulateur pour estimer votre reste à charge.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-[hsl(var(--primary))]">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">Choisissez votre service</h4>
                    <p className="text-xs text-muted-foreground">Ménage ou jardinage</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-[hsl(var(--primary))]">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">Définissez vos besoins</h4>
                    <p className="text-xs text-muted-foreground">Nombre d'heures et fréquence</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-[hsl(var(--primary))]">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">Découvrez votre reste à charge</h4>
                    <p className="text-xs text-muted-foreground">Après déduction du crédit d'impôt 50%</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <CostSimulator />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====== PROCESS ====== */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Comment ça marche ?</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Notre processus</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Comment nous facilitons votre vie en 4 étapes simples</p>
          </motion.div>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
            {PROCESS_STEPS.map((step, i) => (
              <motion.div key={step.num} variants={fadeUp}>
                <Card className="p-6 h-full bg-card border border-border shadow-sm">
                  <span className="font-['Space_Grotesk'] text-3xl font-bold text-[hsl(var(--primary))]/20">{step.num}</span>
                  <h3 className="font-['Space_Grotesk'] font-semibold mt-2 mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ====== AVIS GOOGLE ====== */}
      <GoogleReviewsSection data={googleReviews} />

      {/* ====== TÉMOIGNAGES CLIENTS ====== */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Témoignages</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Nos clients parlent de nous</h2>
            {reviewStats.total > 0 && (
              <div className="flex items-center justify-center gap-3 mt-2">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className={`h-5 w-5 ${s <= Math.round(reviewStats.average) ? 'fill-amber-400 text-amber-400' : 'fill-none text-gray-300'}`} />
                  ))}
                </div>
                <span className="text-sm font-semibold">{reviewStats.average}/5</span>
                <span className="text-sm text-muted-foreground">({reviewStats.total} avis)</span>
              </div>
            )}
          </motion.div>

          {reviews.length > 0 ? (
            <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
              {reviews.slice(0, 6).map((review) => (
                <motion.div key={review._id} variants={fadeUp}>
                  <Card className="p-5 h-full">
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`h-4 w-4 ${s <= review.note ? 'fill-amber-400 text-amber-400' : 'fill-none text-gray-300'}`} />
                      ))}
                    </div>
                    {review.titre && <h3 className="font-semibold text-sm mb-1">{review.titre}</h3>}
                    <p className="text-sm text-muted-foreground leading-relaxed mb-3">« {review.commentaire} »</p>
                    <span className="text-xs font-medium">{review.nom}</span>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">Découvrez bientôt les témoignages de nos clients satisfaits.</p>
            </div>
          )}

          <div className="text-center mt-8">
            <Link to="/temoignages">
              <Button variant="outline" size="lg">
                {reviews.length > 0 ? 'Voir tous les avis' : 'Laisser un avis'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ====== VILLES DESSERVIES (SEO local) ====== */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/60" data-testid="home-cities-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <div>
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Zone d'intervention</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Ménage et jardinage près de chez vous</h2>
              <p className="text-muted-foreground max-w-xl">Notre agence de Servins intervient dans un rayon de 40 km dans tout l'Artois. Retrouvez votre commune :</p>
            </div>
            <Link to="/services-a-domicile">
              <Button variant="outline">Toutes les villes desservies <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          </motion.div>
          <div className="flex flex-wrap gap-2">
            {getMainCities().map((c) => (
              <Link key={c.slug} to={cityPath(c)} className="px-4 py-2 rounded-full border border-border bg-card text-sm font-medium hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors" data-testid="home-city-link">
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ====== BLOG ====== */}
      {blogPosts.length > 0 && (
        <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60" data-testid="home-blog-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <motion.div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <div>
                <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Le blog</Badge>
                <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Nos derniers articles de fond</h2>
                <p className="text-muted-foreground max-w-xl">Guides complets sur l'entretien de la maison, le jardin, le crédit d'impôt et la vie pratique.</p>
              </div>
              <Link to="/blog"><Button variant="outline">Tous les articles <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogPosts.map((post) => <BlogCard key={post.id} post={post} />)}
            </div>
          </div>
        </section>
      )}

      {/* ====== FAQ ====== */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">FAQ</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Questions fréquentes</h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp} data-testid="faq">
            <Accordion type="single" collapsible className="space-y-3">
              {FAQ_ITEMS.map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`} className="border border-border rounded-lg px-4 bg-card" data-testid="faq-item">
                  <AccordionTrigger className="text-sm font-medium text-left hover:no-underline">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
          <div className="text-center mt-8">
            <Link to="/credit-impot">
              <Button variant="outline">
                En savoir plus sur l'avance immédiate
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ====== FINAL CTA ====== */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--primary))] text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Facilitez-vous la vie dès aujourd'hui</h2>
            <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">
              Ménage à partir de 14,99 €/h, jardinage à partir de 24,95 €/h après avance immédiate. Contactez-nous pour un devis gratuit et sans engagement.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/devis">
                <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href={`tel:${COMPANY_INFO.phoneRaw}`}>
                <Button size="lg" variant="secondary">
                  <Phone className="mr-2 h-4 w-4" />
                  {COMPANY_INFO.phone}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
